import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { listTimeZones } from 'timezone-support';
import { Step, Icon, Form as SemanticForm, Button, Loader, Dimmer } from 'semantic-ui-react';
import { Form, Input, Dropdown, Checkbox } from '../../../../../components';

const timeZones = listTimeZones();
const timeZonesOptions = timeZones.map(item => ({ key: item, value: item, text: item }));
const schema = Yup.object().shape({
  cnpj: Yup.number().required(),
  name: Yup.string().required(),
  timezone: Yup.string().oneOf(timeZones).required(),
  isContract: Yup.boolean(),
  classification: Yup.string().required(),
  category: Yup.string(),
  location: Yup.object().shape({
    address: Yup.object().shape({
      street: Yup.string().required(),
      number: Yup.number().required(),
      neighborhood: Yup.string().required(),
      postalCode: Yup.number().required(),
      city: Yup.string().required(),
      state: Yup.string().required()
    }).required(),
    coordinates: Yup.array().of(Yup.number()).required().min(2).max(2)
  }).required()
});

const classificationOptions = [
  { key: 1, value: 'A+', text: 'A+' },
  { key: 2, value: 'A', text: 'A' },
  { key: 3, value: 'B', text: 'B' },
  { key: 4, value: 'C', text: 'C' }
]

const categoryOptions = [
  { key: 1, value: "CONDO", text: "Condomínio" },
  { key: 2, value: "HOTEL", text: "Hotel" },
  { key: 3, value: "RESORT", text: "Resort" },
  { key: 4, value: "CORPORATE", text: "Corporativo" },
  { key: 5, value: "SCHOOL", text: "Escola" },
  { key: 6, value: "SHOPPING", text: "Shopping" },
  { key: 7, value: "OTHER", text: "Outros" }
]

const GeneralStep = ({ active, completed }) => {
  const { t } = useTranslation();

  return (
    <Step active={active} completed={completed}>
      <Icon name='info' />
      <Step.Content>
        <Step.Title>{t('BUILDINGS.NEW.GENERAL_STEP.TITLE')}</Step.Title>
        <Step.Description>{t('BUILDINGS.NEW.GENERAL_STEP.DESCRIPTION')}</Step.Description>
      </Step.Content>
    </Step>
  );
};

const GeneralContent = ({ active, building, companies, prev, next }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [companyIsActive, setCompanyIsActive] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [coordinates, setCoordinates] = useState([
    0, 0
  ]);

  useEffect(() => {
    const checkTheSelectedCompany = ({ building, companies }) => {
      const companySelected = companies.filter((company) => company.value === building.company)[0]?.text;
      setCompanyIsActive(companySelected);
    };

    checkTheSelectedCompany({ building, companies });
  }, [building, companies]);

  /**@deprecated necessário chave de api do google para funcionamento*/
  // const fetchCoordinates = async () => {
  //   const fullAddress = `${street} ${number}, ${neighborhood}, ${city} - ${state}`;
  //   const encoded = encodeURIComponent(fullAddress);

  //   const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encoded}&&key=AIzaSyBOE4orybxkxQJcjKCGHxNli8KbhkwIpbw`);
  //   const resJson = await res.json();

  //   if (resJson.status === 'OK') {
  //     const { lat, lng } = resJson.results[0].geometry.location;
  //     setCoordinates([lat, lng])
  //   } else {
  //     alert('Erro ao buscar coordenadas')
  //   }
  // };

  const handleCep = async (value) => {
    if (!value) return;
    if (value.length < 8) return;
    setLoading(true)
    const encoded = encodeURIComponent(value);
    const response = await fetch(`https://viacep.com.br/ws/${encoded}/json/`);
    const resJson = await response.json();

    if (!resJson) return;

    setState(resJson.uf);

    if (resJson.localidade && resJson.localidade !== '') setCity(resJson.localidade);
    if (resJson.bairro && resJson.bairro !== '') setNeighborhood(resJson.bairro);
    if (resJson.logradouro && resJson.logradouro !== '') setStreet(resJson.logradouro);

    setLoading(false)
  }

  return active && (
    <Form initialData={building} onSubmit={next} schema={schema}>
      <Dimmer inverted active={loading}>
        <Loader />
      </Dimmer>
      <SemanticForm.Group widths='equal'>
        <Checkbox
          label={t('BUILDINGS.NEW.GENERAL_STEP.IS_CONTRACT.LABEL')}
          name="isContract"
          toggle
        />
        <Dropdown
          fluid
          selection
          name='classification'
          label={t('BUILDINGS.NEW.GENERAL_STEP.CLASSIFICATIONS.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.CLASSIFICATIONS.PLACEHOLDER')}
          options={classificationOptions}
        />
      </SemanticForm.Group>
      {
        companyIsActive === "Edifica" ?
          <Dropdown
            fluid
            selection
            name='category'
            label={t('BUILDINGS.NEW.GENERAL_STEP.CATEGORY.LABEL')}
            placeholder={t('BUILDINGS.NEW.GENERAL_STEP.CATEGORY.PLACEHOLDER')}
            options={categoryOptions}
          />
          :
          <></>
      }
      <Input
        name='name'
        label={t('BUILDINGS.NEW.GENERAL_STEP.NAME.LABEL')}
        placeholder={t('BUILDINGS.NEW.GENERAL_STEP.NAME.PLACEHOLDER')}
      />
      <SemanticForm.Group widths='equal'>
        <Input
          type='number'
          name='cnpj'
          label={t('BUILDINGS.NEW.GENERAL_STEP.CNPJ.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.CNPJ.PLACEHOLDER')}
        />
        <Dropdown
          fluid
          selection
          name='timezone'
          label={t('BUILDINGS.NEW.GENERAL_STEP.TIME_ZONE.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.TIME_ZONE.PLACEHOLDER')}
          options={timeZonesOptions}
        />
      </SemanticForm.Group>
      <SemanticForm.Group widths='equal'>
        <Input
          type='text'
          name='location.address.postalCode'
          label={t('BUILDINGS.NEW.GENERAL_STEP.POSTAL_CODE.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.POSTAL_CODE.PLACEHOLDER')}
          onChange={(ev) => handleCep(ev.target.value)}
          maxLength={8}
        />
        <Input
          onChange={(ev) => setState(ev.target.value)}
          name='location.address.state'
          label={t('BUILDINGS.NEW.GENERAL_STEP.STATE.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.STATE.PLACEHOLDER')}
          disabled
          defaultValue={state}
        />
      </SemanticForm.Group>
      <SemanticForm.Group widths='equal'>
        <Input
          onChange={(ev) => setCity(ev.target.value)}
          name='location.address.city'
          label={t('BUILDINGS.NEW.GENERAL_STEP.CITY.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.CITY.PLACEHOLDER')}
          disabled
          defaultValue={city}
        />
        <Input
          onChange={(ev) => setNeighborhood(ev.target.value)}
          name='location.address.neighborhood'
          label={t('BUILDINGS.NEW.GENERAL_STEP.NEIGHBORHOOD.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.NEIGHBORHOOD.PLACEHOLDER')}
          value={neighborhood}
        />
      </SemanticForm.Group>
      <SemanticForm.Group widths='equal'>
        <Input
          onChange={(ev) => setStreet(ev.target.value)}
          name='location.address.street'
          label={t('BUILDINGS.NEW.GENERAL_STEP.STREET.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.STREET.PLACEHOLDER')}
          defaultValue={street}
        />
        <Input
          onChange={(ev) => setNumber(ev.target.value)}
          type='number'
          name='location.address.number'
          label={t('BUILDINGS.NEW.GENERAL_STEP.NUMBER.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.NUMBER.PLACEHOLDER')}
        />
      </SemanticForm.Group>
      {/* <SemanticForm.Group widths='equal'>
        <Button type="button" secondary onClick={fetchCoordinates}>{t('Buscar coordenadas')}</Button>
      </SemanticForm.Group> */}
      <SemanticForm.Group widths='equal'>
        <Input
          type='number'
          step='any'
          value={coordinates[0]}
          readOnly
          name='location.coordinates[0]'
          label={t('BUILDINGS.NEW.GENERAL_STEP.LATITUDE.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.LATITUDE.PLACEHOLDER')}
        />
        <Input
          type='number'
          step='any'
          value={coordinates[1]}
          readOnly
          name='location.coordinates[1]'
          label={t('BUILDINGS.NEW.GENERAL_STEP.LONGITUDE.LABEL')}
          placeholder={t('BUILDINGS.NEW.GENERAL_STEP.LONGITUDE.PLACEHOLDER')}
        />
      </SemanticForm.Group>
      <Button primary onClick={prev}>{t('BUILDINGS.NEW.PREV')}</Button>
      <Button primary type='submit'>{t('BUILDINGS.NEW.NEXT')}</Button>
    </Form>
  );
};

export { GeneralStep, GeneralContent };
